<template>
  <div
    v-if="isNotifyStage"
    class="d-flex align-center"
    data-testid="draw-notify-action"
  >
    <SoTooltip :disabled="canNotify" :text="disabledReason">
      <SoButton
        title="Notify winners"
        type="primary"
        :disabled="!canNotify"
        data-testid="notify-winners-btn"
        @click="showNotifyDialog = true"
      />
    </SoTooltip>

    <SoDialog
      v-model="showNotifyDialog"
      card-title="Notify winners?"
      :max-width="480"
      data-testid="notify-winners-dialog"
    >
      <p>
        Every selected winner will see the "you won" modal in their online
        account straight away, ahead of the scheduled notify winners date.
      </p>
      <p>
        Winners can only be notified once. Any winners selected after this point
        will need to be notified manually.
      </p>
      <p class="text-medium-emphasis mt-4">
        This does not email the winners — marketing sends that separately.
      </p>
      <div class="d-flex justify-end mt-4">
        <SoButton
          title="Cancel"
          class="mr-2"
          large
          @click="showNotifyDialog = false"
        />
        <SoButton
          title="Notify winners"
          type="primary"
          large
          :loading="notifyMutation.isPending"
          data-testid="notify-winners-confirm-btn"
          @click="confirmNotify"
        />
      </div>
    </SoDialog>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import { useSnackbarStore } from "@/stores/snackbar"
  import errorHandler from "@/services/ErrorService/errorHandler"
  import { LoyaltyMutator } from "@/mutators/loyalty/LoyaltyMutator"
  import {
    PrizeDrawEntryWinnerStatus,
    PrizeDrawStatus,
  } from "@/api/backofficeRest/generated/model"
  import { IDrawWinner } from "@/models/operations/draw/drawWinner.types"
  import { IDrawReward } from "@/models/operations/draw/drawReward.types"
  import { unfilledRejectedSlots } from "@/helpers/drawWinnerHelper"

  // Statuses (once entry has closed) where Notify shows as a disabled preview
  // before winners are calculated — so the upcoming action is discoverable.
  const PRE_NOTIFY_STATUSES: ReadonlySet<PrizeDrawStatus> = new Set([
    PrizeDrawStatus.ENTRY_CLOSED,
    PrizeDrawStatus.CALCULATING_WINNERS,
  ])

  export default defineComponent({
    components: { SoButton, SoDialog, SoTooltip },
    props: {
      drawId: {
        type: String,
        required: true,
      },
      status: {
        type: String as PropType<PrizeDrawStatus | null>,
        default: null,
      },
      winners: {
        type: Array as PropType<IDrawWinner[]>,
        default: () => [],
      },
      rewards: {
        type: Array as PropType<IDrawReward[]>,
        default: () => [],
      },
    },
    emits: ["changed"],
    data() {
      return {
        snackbar: useSnackbarStore(),
        notifyMutation: LoyaltyMutator.notifyWinners(),
        showNotifyDialog: false,
      }
    },
    computed: {
      // Winners are calculated and notify is the live action. UNKNOWN = publication
      // date passed before notify ran (notified_at still null), still awaiting notify.
      awaitingNotify(): boolean {
        return (
          this.status === PrizeDrawStatus.WINNERS_CALCULATED ||
          this.status === PrizeDrawStatus.UNKNOWN
        )
      },
      // Shared with OpsDrawRerunAction so notify/rerun agree on unfilled slots.
      unresolvedRejectedSlots(): number {
        return unfilledRejectedSlots(this.winners, this.rewards)
      },
      // Rewards must be loaded to check slots; Notify is one-way so fail closed.
      allSlotsFilled(): boolean {
        return (
          this.winners.some(
            (winner) => winner.status === PrizeDrawEntryWinnerStatus.SELECTED,
          ) &&
          this.rewards.length > 0 &&
          this.unresolvedRejectedSlots === 0
        )
      },
      canNotify(): boolean {
        return this.awaitingNotify && this.allSlotsFilled
      },
      // Visible from entry-close as a disabled preview through the awaiting-notify
      // window; hidden once notified (awaitingNotify excludes WINNERS_NOTIFIED+).
      isNotifyStage(): boolean {
        return (
          this.awaitingNotify ||
          (this.status !== null && PRE_NOTIFY_STATUSES.has(this.status))
        )
      },
      disabledReason(): string {
        if (this.canNotify) return ""
        if (this.awaitingNotify && this.unresolvedRejectedSlots > 0) {
          return "Resolve rejected slots before notifying"
        }
        return "Select winners before notifying"
      },
    },
    methods: {
      async confirmNotify() {
        try {
          await this.notifyMutation.mutateAsync({ drawId: this.drawId })
        } catch (error) {
          errorHandler.showCaughtError(
            error,
            "Failed to notify winners. Please try again.",
          )
          return
        }
        this.snackbar.addSuccess("Winners marked as notified")
        this.showNotifyDialog = false
        this.$emit("changed")
      },
    },
  })
</script>
